/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { Options } from '../options.interface';
import { UserType } from '../suggestions-api/suggestions.webservice';
import { FIBII_API_CONFIG } from './fibii-api.config';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Appointment,
  ArchivedReason,
  Cco,
  ClientWithBankIds,
  Dossier,
  DossierStatusEnum,
  LocaleEnum,
  UsedDataDropito,
} from '@wizbii/utils/models';
import { Observable } from 'rxjs';

@Injectable()
export class FibiiWebservice {
  readonly #httpClient = inject(HttpClient);
  readonly #fileApiConfig = inject(FIBII_API_CONFIG);

  readonly #baseUrl = this.#fileApiConfig.baseUrl;

  updateDossierStatus(
    userId: string,
    suggestionId: string,
    status: DossierStatusEnum,
    options?: Options
  ): Observable<Dossier> {
    return this.#httpClient.patch<Dossier>(
      `${this.#baseUrl}/dossiers/${userId}/client/${suggestionId}`,
      { status },
      options
    );
  }

  updateDossierStatusV2(
    userId: string,
    suggestionId: string,
    status: DossierStatusEnum,
    options?: Options
  ): Observable<Dossier> {
    return this.#httpClient.patch<Dossier>(
      `${this.#baseUrl}/v2/dossiers/${userId}/client/${suggestionId}`,
      { status },
      options
    );
  }

  updateDossierV2(
    userId: string,
    suggestionId: string,
    body: Partial<Dossier>,
    options?: Options
  ): Observable<Dossier> {
    return this.#httpClient.patch<Dossier>(
      `${this.#baseUrl}/v2/dossiers/${userId}/client/${suggestionId}`,
      body,
      options
    );
  }

  updateDossierArchiveStatus(
    userId: string,
    suggestionId: string,
    isArchived: boolean,
    reason: string,
    options?: Options
  ): Observable<Dossier> {
    return this.#httpClient.patch<Dossier>(
      `${this.#baseUrl}/v2/dossiers/${userId}/client/${suggestionId}/archive`,
      { isArchived, reason },
      options
    );
  }

  getDossiers(): Observable<Dossier[]> {
    return this.#httpClient.get<Dossier[]>(`${this.#baseUrl}/dossiers`);
  }

  validateDepotInformations(userId: string, suggestionId: string): Observable<Dossier> {
    return this.#httpClient.patch<Dossier>(`${this.#baseUrl}/dossiers/${userId}/mandate/${suggestionId}`, {});
  }

  getNextSuggestionToUpdate(userId: string): Observable<{ suggestionId: string }> {
    return this.#httpClient.get<{ suggestionId: string }>(`${this.#baseUrl}/dossiers/update-data/${userId}`);
  }

  setDossierUpdated(userId: string, status: string, suggestionIds: string[]): Observable<void> {
    return this.#httpClient.patch<void>(`${this.#baseUrl}/v2/dossiers/${userId}`, {
      status,
      suggestionIds,
    });
  }

  getQuestionsUpdatedBySuggestionId(userId: string, suggestionId: string): Observable<string[]> {
    return this.#httpClient.get<string[]>(`${this.#baseUrl}/dossiers/updated/${userId}/${suggestionId}`);
  }

  getQuestionAndResponseWithDropitoEngine(
    helpId: string,
    userId: string,
    locale: LocaleEnum = LocaleEnum.fr_FR
  ): Observable<UsedDataDropito[]> {
    return this.#httpClient.get<UsedDataDropito[]>(`${this.#baseUrl}/dropito/used-data/${locale}/${helpId}/${userId}`);
  }

  getNextQuestionWithDropitoEngine(helpId: string, userId: string): Observable<{ previous: string; next: string }> {
    return this.#httpClient.get<{ previous: string; next: string }>(
      `${this.#baseUrl}/dropito/next-question/${helpId}/${userId}`
    );
  }

  getUserComplementaryFormState(
    suggestionId: string,
    userId: string,
    questionId?: string,
    userType: UserType = 'member'
  ): Observable<{ next?: string; previous?: string }> {
    return this.#httpClient.post<{ next: string }>(
      `${this.#baseUrl}/additional-questions/state/${userType}/${userId}/${suggestionId}`,
      {
        ...(!!questionId && { currentQuestionId: questionId }),
      }
    );
  }

  getDossiersByClientId(clientId: string): Observable<Dossier[]> {
    return this.#httpClient.get<Dossier[]>(`${this.#baseUrl}/v2/dossiers/client/${clientId}`);
  }

  getArchivedReasons(): Observable<ArchivedReason[]> {
    return this.#httpClient.get<ArchivedReason[]>(`${this.#baseUrl}/archived-reasons`);
  }

  getArchivedReasonById(reasonId: string, lang: string): Observable<ArchivedReason> {
    return this.#httpClient.get<ArchivedReason>(`${this.#baseUrl}/archived-reasons/${reasonId}?language=${lang}`);
  }

  getPublicCco(userId: string): Observable<Cco> {
    return this.#httpClient.get<Cco>(`${this.#baseUrl}/cco/public/${userId}`);
  }

  getDossierBySuggestionId(suggestionId: string): Observable<Dossier> {
    return this.#httpClient.get<Dossier>(`${this.#baseUrl}/dossiers/${suggestionId}`);
  }

  getClientBankIds(userId: string): Observable<ClientWithBankIds | null> {
    return this.#httpClient.get<ClientWithBankIds>(`${this.#baseUrl}/external/clients/${userId}`);
  }

  patchClientBankIds(userId: string, actualBankIds: string[]): Observable<ClientWithBankIds> {
    return this.#httpClient.patch<ClientWithBankIds>(`${this.#baseUrl}/external/clients/${userId}`, { actualBankIds });
  }

  updateAppointmentStatus(
    appointmentId: string,
    payload: { status?: string; cancellationReason?: string },
    options?: Options
  ): Observable<Appointment> {
    return this.#httpClient.patch<Appointment>(`${this.#baseUrl}/appointments/${appointmentId}`, payload, options);
  }

  getNextAppointmentByOrderId(orderId: string): Observable<Appointment> {
    return this.#httpClient.get<Appointment>(`${this.#baseUrl}/orders/${orderId}/appointments/next`);
  }

  cancelAppointment(appointmentId: string, cancellationReason: string): Observable<Appointment> {
    return this.#httpClient.post<Appointment>(`${this.#baseUrl}/appointments/${appointmentId}/cancel`, {
      cancellationReason: cancellationReason,
    });
  }
}
